import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase, MapPin, Clock, DollarSign, Filter, ArrowRight,
  Building2, Shield, MessageCircle, ChevronRight, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import TrustSignalStrip from '@/components/common/TrustSignalStrip';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta, useServiceSchema, useNoIndex } from '@/hooks/useSEO';
import { trackEvent } from '@/services/analytics';

/* ─────────────────────────────────────────────────────────────────────
 *  HOT JOBS DATA — Curated current openings
 *  When CMS-backed jobs API is wired, swap this static array for
 *  `useEffect(() => fetch('/api/jobs/active'))`.
 * ─────────────────────────────────────────────────────────────────── */
const JOBS = [
  {
    id: 'sa-construction-foreman-2026-01',
    title: 'Construction Foreman',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    city: 'Riyadh',
    industry: 'Construction',
    salary: 'SAR 4,500 – 6,000/mo',
    inrSalary: '₹1,00,000 – 1,35,000/mo',
    duration: '2-year contract · renewable',
    experience: '5+ years',
    accommodation: 'Provided + transport + medical',
    openings: 24,
    urgency: 'urgent',
    qualifications: 'ITI / Diploma · English working knowledge',
    deployBy: 'Within 30 days',
  },
  {
    id: 'ae-hvac-tech-2026-02',
    title: 'HVAC Technician',
    country: 'UAE',
    flag: '🇦🇪',
    city: 'Dubai · Abu Dhabi',
    industry: 'Oil & Gas',
    salary: 'AED 3,500 – 5,200/mo',
    inrSalary: '₹80,000 – 1,18,000/mo',
    duration: '2-year contract',
    experience: '3+ years',
    accommodation: 'Bachelor accommodation + visa + flights',
    openings: 18,
    urgency: 'high',
    qualifications: 'ITI Refrigeration / HVAC certification',
    deployBy: 'Within 45 days',
  },
  {
    id: 'qa-stewardess-2026-03',
    title: 'F&B Steward / Waiter',
    country: 'Qatar',
    flag: '🇶🇦',
    city: 'Doha',
    industry: 'Hospitality',
    salary: 'QAR 1,800 – 2,400/mo',
    inrSalary: '₹42,000 – 56,000/mo',
    duration: '2-year contract',
    experience: '1+ year preferred · freshers OK',
    accommodation: 'Sharing + duty meals + medical',
    openings: 40,
    urgency: 'urgent',
    qualifications: '10+2 minimum · basic English',
    deployBy: 'Within 21 days',
  },
  {
    id: 'sa-staff-nurse-2026-04',
    title: 'Staff Nurse (GNM / BSc Nursing)',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    city: 'Jeddah · Riyadh · Dammam',
    industry: 'Healthcare',
    salary: 'SAR 6,000 – 9,500/mo',
    inrSalary: '₹1,35,000 – 2,15,000/mo',
    duration: '2-year contract · renewable',
    experience: '2+ years post-registration',
    accommodation: 'Hospital accommodation + medical + flights',
    openings: 32,
    urgency: 'urgent',
    qualifications: 'Prometric pass · DataFlow ready',
    deployBy: 'Within 60 days',
  },
  {
    id: 'kw-pipe-fitter-2026-05',
    title: 'Pipe Fitter / Welder 6G',
    country: 'Kuwait',
    flag: '🇰🇼',
    city: 'Ahmadi · Shuaiba',
    industry: 'Oil & Gas',
    salary: 'KWD 250 – 380/mo',
    inrSalary: '₹70,000 – 1,06,000/mo',
    duration: '2-year contract',
    experience: '4+ years (offshore preferred)',
    accommodation: 'Camp accommodation + duty food + transport',
    openings: 16,
    urgency: 'high',
    qualifications: 'ITI Welding / Fitting · 6G certificate',
    deployBy: 'Within 45 days',
  },
  {
    id: 'om-driver-heavy-2026-06',
    title: 'Heavy Vehicle / Trailer Driver',
    country: 'Oman',
    flag: '🇴🇲',
    city: 'Muscat · Sohar',
    industry: 'Logistics',
    salary: 'OMR 200 – 320/mo',
    inrSalary: '₹45,000 – 72,000/mo',
    duration: '2-year contract',
    experience: '3+ years GCC license preferred',
    accommodation: 'Sharing + meal allowance',
    openings: 28,
    urgency: 'medium',
    qualifications: 'Heavy/Trailer license · clean record',
    deployBy: 'Within 30 days',
  },
  {
    id: 'sa-civil-engineer-2026-07',
    title: 'Civil Site Engineer',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    city: 'NEOM · Riyadh megaprojects',
    industry: 'Construction',
    salary: 'SAR 8,000 – 13,000/mo',
    inrSalary: '₹1,80,000 – 2,95,000/mo',
    duration: '2-year contract · renewable',
    experience: '5+ years (megaproject preferred)',
    accommodation: 'Family/bachelor + medical + flights',
    openings: 12,
    urgency: 'high',
    qualifications: 'B.E./B.Tech Civil · Saudi Aramco SAP knowledge a plus',
    deployBy: 'Within 60 days',
  },
  {
    id: 'bh-electrical-tech-2026-08',
    title: 'Electrical Technician',
    country: 'Bahrain',
    flag: '🇧🇭',
    city: 'Manama · Sitra',
    industry: 'Maintenance',
    salary: 'BHD 220 – 310/mo',
    inrSalary: '₹50,000 – 72,000/mo',
    duration: '2-year contract',
    experience: '2+ years',
    accommodation: 'Sharing + duty meals',
    openings: 14,
    urgency: 'medium',
    qualifications: 'ITI Electrician · BTC card a plus',
    deployBy: 'Within 45 days',
  },
];

const COUNTRIES = ['All', 'Saudi Arabia', 'UAE', 'Qatar', 'Kuwait', 'Oman', 'Bahrain'];
const INDUSTRIES = ['All', 'Construction', 'Oil & Gas', 'Hospitality', 'Healthcare', 'Logistics', 'Maintenance'];

const URGENCY_STYLE = {
  urgent:  { label: 'URGENT',     classes: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30' },
  high:    { label: 'HIGH PRIORITY', classes: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30' },
  medium:  { label: 'OPEN',       classes: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30' },
};

/* ─── JobPosting JSON-LD schema injection ─────────────────────────── */
const injectJobSchemas = () => {
  const today = new Date();
  const datePosted = today.toISOString().split('T')[0];
  // Postings are evergreen — refreshed every 30 days. Required by Google JobPosting spec.
  const validThrough = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const schemas = JOBS.map((j) => ({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: `${j.title} — ${j.country}`,
    description:
      `Hiring ${j.openings} ${j.title.toLowerCase()} for ${j.country} ${j.industry} sector. ` +
      `${j.experience} experience required. Salary ${j.salary} (≈ ${j.inrSalary}). ` +
      `${j.accommodation}. ${j.qualifications}.`,
    datePosted,
    validThrough,
    directApply: true,
    employmentType: 'FULL_TIME',
    industry: j.industry,
    hiringOrganization: {
      '@type': 'Organization',
      '@id': 'https://zafco.in/#organization',
      name: 'Zafco Human Resource Management',
      sameAs: 'https://zafco.in',
    },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressCountry: j.country, addressLocality: j.city },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: j.salary.split(' ')[0],
      value: { '@type': 'QuantitativeValue', value: j.salary, unitText: 'MONTH' },
    },
    totalJobOpenings: j.openings,
    applicantLocationRequirements: { '@type': 'Country', name: 'India' },
    jobImmediateStart: j.urgency === 'urgent',
  }));

  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.setAttribute('data-hotjobs-schema', '1');
  el.text = JSON.stringify(schemas)
    .replace(/<\/(script)/gi, '<\\/$1')
    .replace(/<!--/g, '<\\!--');
  document.head.appendChild(el);

  return () => {
    document.head.querySelector('script[data-hotjobs-schema]')?.remove();
  };
};

/* ─── Job Card ────────────────────────────────────────────────────── */
const JobCard = ({ job }) => {
  const urgency = URGENCY_STYLE[job.urgency];
  const waText = `Hello Zafco, I would like to apply for the ${job.title} role in ${job.country} (Job ID: ${job.id}).`;
  const waHref =
    `https://wa.me/919958848998?text=${encodeURIComponent(waText)}` +
    `&utm_source=hot-jobs&utm_medium=whatsapp&utm_campaign=job-apply&utm_content=${job.id}`;

  const handleApply = () => {
    trackEvent('hot_job_apply', {
      category: 'lead_generation',
      job_id: job.id,
      job_title: job.title,
      country: job.country,
      industry: job.industry,
      source: 'hot-jobs-card',
    });
  };

  return (
    <motion.article
      className="group bg-slate-900/50 border border-white/5 hover:border-[#C5A028]/40 rounded-2xl p-5 md:p-6 flex flex-col transition-all"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      data-testid={`job-card-${job.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
          <span className="text-base" aria-hidden="true">{job.flag}</span>
          <span>{job.country}</span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${urgency.classes}`}>
          {urgency.label}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-lg md:text-xl font-bold font-syne text-white leading-tight mb-1">
        {job.title}
      </h3>
      <p className="text-[#C5A028] text-xs font-semibold uppercase tracking-wider mb-4">{job.industry}</p>

      {/* Stats */}
      <dl className="space-y-2 text-sm flex-1">
        <div className="flex items-start gap-2">
          <DollarSign className="w-4 h-4 text-[#C5A028] flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <dt className="sr-only">Salary</dt>
            <dd className="text-white font-semibold leading-tight">{job.salary}</dd>
            <dd className="text-slate-400 text-xs">≈ {job.inrSalary}</dd>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <dd className="text-slate-300 leading-tight">{job.city}</dd>
        </div>
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <dd className="text-slate-300 leading-tight">{job.duration} · {job.experience}</dd>
        </div>
        <div className="flex items-start gap-2">
          <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <dd className="text-slate-300 leading-tight text-xs">{job.accommodation}</dd>
        </div>
      </dl>

      {/* Footer */}
      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Users className="w-3.5 h-3.5 text-[#C5A028]" aria-hidden="true" />
          <span><strong className="text-white">{job.openings}</strong> openings</span>
        </div>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleApply}
          className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-slate-900 font-bold text-xs px-3.5 py-2 rounded-full transition-colors"
          data-testid={`job-apply-${job.id}`}
        >
          <MessageCircle className="w-3.5 h-3.5" fill="white" /> Apply
        </a>
      </div>
    </motion.article>
  );
};

/* ─── Filter Pill ─────────────────────────────────────────────────── */
const FilterPill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-xs sm:text-sm font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all ${
      active
        ? 'bg-[#C5A028] text-black'
        : 'bg-slate-900/60 text-slate-300 border border-white/10 hover:border-[#C5A028]/40 hover:text-white'
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

/* ─── Page ────────────────────────────────────────────────────────── */
const HotJobsPage = () => {
  // Thin utility/listing template — flagged in GSC "Crawled - currently not indexed"
  // cleanup (Feb 2026). Noindex + follow keeps internal-link equity flowing but
  // removes the page from Google's index so crawl-budget isn't wasted on it.
  useNoIndex({ follow: true });

  usePageMeta(
    'Hot Jobs Abroad 2026 | Saudi, UAE, Qatar & Gulf Jobs — Zafco HR (MEA Approved)',
    'Latest overseas jobs for Indians in Saudi, UAE, Qatar, Kuwait, Oman & Bahrain — construction, oil & gas, healthcare, hospitality. MEA-approved. Apply via WhatsApp.'
  );

  useServiceSchema({
    name: 'Overseas Recruitment for Gulf Jobs',
    description:
      'MEA-approved direct-employer recruitment for skilled and semi-skilled workers across Gulf states. ' +
      'Zero candidate fee (employer-sponsored), 21-60 day deployment, 8 active job categories.',
    serviceType: 'Overseas Recruitment',
    areaServed: ['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Oman', 'Bahrain'],
    url: 'https://zafco.in/hot-jobs',
  });

  useEffect(() => injectJobSchemas(), []);

  const [country, setCountry] = useState('All');
  const [industry, setIndustry] = useState('All');

  const filteredJobs = useMemo(() => {
    return JOBS.filter((j) => {
      if (country !== 'All' && j.country !== country) return false;
      if (industry !== 'All' && j.industry !== industry) return false;
      return true;
    });
  }, [country, industry]);

  const totalOpenings = JOBS.reduce((sum, j) => sum + j.openings, 0);

  return (
    <div className="bg-slate-950" data-testid="hot-jobs-page">

      <PageHeader
        title="Hot Jobs Abroad — Saudi, UAE, Qatar & Gulf Opportunities"
        subtitle={`${totalOpenings}+ live openings · MEA-approved recruitment · Direct employer interviews · Free for candidates (employer-sponsored)`}
        breadcrumbs={[{ label: 'Hot Jobs' }]}
        keywordsTier={['MEA Approved', 'Gulf · Saudi · UAE', `${totalOpenings}+ Openings`]}
      />

      <TrustSignalStrip />

      {/* ============ FILTER BAR ============ */}
      <section className="sticky top-16 z-30 bg-slate-950/95 backdrop-blur-xl border-y border-white/5" data-testid="hot-jobs-filters">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-[#C5A028]" aria-hidden="true" />
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Filter Openings</span>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-2 px-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap pr-1">Country:</span>
              {COUNTRIES.map((c) => (
                <FilterPill
                  key={c}
                  active={country === c}
                  onClick={() => setCountry(c)}
                  testId={`filter-country-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c}
                </FilterPill>
              ))}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-2 px-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap pr-1">Industry:</span>
              {INDUSTRIES.map((i) => (
                <FilterPill
                  key={i}
                  active={industry === i}
                  onClick={() => setIndustry(i)}
                  testId={`filter-industry-${i.toLowerCase().replace(/[\s&]+/g, '-')}`}
                >
                  {i}
                </FilterPill>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ JOBS GRID ============ */}
      <section className="py-12 md:py-16" aria-labelledby="jobs-heading">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <h2 id="jobs-heading" className="text-xl md:text-2xl font-bold text-white font-syne leading-tight">
              {filteredJobs.length} live opening{filteredJobs.length === 1 ? '' : 's'}
              {country !== 'All' && ` in ${country}`}
              {industry !== 'All' && ` · ${industry}`}
            </h2>
            <Link
              to="/contact?source=hot-jobs-bulk"
              className="text-[#C5A028] hover:text-[#EAB308] text-sm font-semibold inline-flex items-center gap-1 transition-colors"
              data-testid="hot-jobs-bulk-link"
            >
              Hiring 50+? Talk to us <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {filteredJobs.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="jobs-grid">
              {filteredJobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          ) : (
            <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-12 text-center" data-testid="jobs-empty-state">
              <Briefcase className="w-10 h-10 text-slate-500 mx-auto mb-4" aria-hidden="true" />
              <p className="text-white font-semibold mb-2">No openings match your filter.</p>
              <p className="text-slate-400 text-sm mb-5">Tell us your profile and we'll match you with upcoming roles.</p>
              <Button
                onClick={() => { setCountry('All'); setIndustry('All'); }}
                className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold rounded-full px-6"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ============ WHY APPLY THROUGH ZAFCO ============ */}
      <section className="py-12 md:py-16 bg-slate-900/30 border-y border-white/5" aria-labelledby="why-apply-heading">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-[#C5A028] font-semibold text-xs tracking-widest uppercase mb-2">Why Apply Through Us</p>
            <h2 id="why-apply-heading" className="text-2xl md:text-3xl font-bold text-white font-syne leading-tight">
              Zero candidate fee. Direct employer interviews.
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Shield, title: 'MEA-Approved Agency', desc: 'RC: B-0762/DEL/PER/1000+/5/9148/2015 — recruitment fees paid by employer under e-Migrate.' },
              { icon: Building2, title: 'Direct Employer Channel', desc: 'No middlemen. We are empanelled with the Royal Saudi Embassy in New Delhi.' },
              { icon: Clock, title: '21-60 Day Deployment', desc: 'Most placements close within 30-45 days from interview to onboarding.' },
              { icon: Users, title: '10,000+ Placed', desc: 'Since 2015. Verified track record across all Gulf states.' },
            ].map((p) => (
              <li key={p.title} className="p-5 rounded-2xl bg-slate-900/60 border border-white/5">
                <p.icon className="w-6 h-6 text-[#C5A028] mb-3" aria-hidden="true" />
                <p className="text-white font-bold font-syne text-sm leading-tight mb-1">{p.title}</p>
                <p className="text-slate-400 text-xs leading-relaxed">{p.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ CONSULTATION FORM ============ */}
      <section className="py-16 md:py-20" id="apply-form" aria-labelledby="apply-form-heading">
        <div className="container max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 id="apply-form-heading" className="text-2xl md:text-3xl font-bold text-white font-syne mb-2 leading-tight">
              Don't see your role? Send your profile.
            </h2>
            <p className="text-slate-400 text-sm">
              New openings are added every week. We'll match you with the next batch.
            </p>
          </div>
          <div className="glass-premium rounded-2xl p-6 md:p-7">
            <ConsultationForm variant="dark" />
          </div>
          <p className="text-center text-xs text-slate-400 mt-5">
            By submitting, you agree to receive WhatsApp/SMS updates about matching roles.
            We never share your CV with employers without your written consent.
          </p>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-14 md:py-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border-t border-white/5">
        <div className="container max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-syne mb-4 leading-tight">
            Ready for your overseas career?
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/919958848998?text=Hello%20Zafco,%20I%20saw%20your%20hot%20jobs%20list%20and%20want%20to%20apply.&utm_source=hot-jobs&utm_medium=whatsapp&utm_campaign=final-cta"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('whatsapp_click', { category: 'lead_generation', source: 'hot-jobs-final-cta' })}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-slate-900 font-bold text-base px-7 py-3.5 rounded-full transition-colors"
              data-testid="final-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" fill="white" /> WhatsApp Us
            </a>
            <Button
              asChild
              size="lg"
              className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-base px-7 py-6 rounded-full"
            >
              <a href="#apply-form">
                <ArrowRight className="w-5 h-5 mr-2" /> Send My Profile
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HotJobsPage;
